import React from "react";
import { Box, Button, CircularProgress, Tab, Tabs } from "@mui/material";
import { Patient } from "../../../../services/patient.service";
import AppModal from "../../../../components/ui/AppModal";
import { usePatientForm } from "./use-patient-form";
import TabPanel from "./components/TabPanel";
import PatientInfoTab from "./components/PatientInfoTab";
import PatientGalleryTab from "./components/PatientGalleryTab";
import PatientEventsTab from "./components/PatientEventsTab";

type Props = {
  open: boolean;
  patient?: Patient;
  onClose: () => void;
  onSave: (patient: Patient & { photos?: File[]; removedImageIds?: number[] }) => void;
  isLoading: boolean;
  allTags: string[];
  allPatients?: Patient[];
};

export const PatientForm: React.FC<Props> = ({ open, patient, onClose, onSave, isLoading, allTags, allPatients }) => {
  const {
    tabValue,
    setTabValue,
    photos,
    photoError,
    formData,
    handleChange,
    handleTagsChange,
    handleSelectExisting,
    handleAddEvent,
    handleDeleteEvent,
    handlePhotoChange,
    handleRemovePhoto,
    handleSave,
  } = usePatientForm({ open, patient, onSave });

  const footer = (
    <>
      <Button onClick={onClose} disabled={isLoading} sx={{ height: 46, px: 2.5, color: "text.secondary" }}>
        Cancelar
      </Button>
      <Button
        onClick={handleSave}
        variant="contained"
        disabled={!formData.name || formData.name.trim().length < 4 || isLoading}
        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
        sx={{ height: 46, px: 2.5 }}
      >
        {isLoading ? "Salvando..." : "Salvar paciente"}
      </Button>
    </>
  );

  return (
    <AppModal
      open={open}
      onClose={onClose}
      kicker={patient ? "Edição" : "Cadastro"}
      title={patient ? "Editar paciente" : "Novo paciente"}
      subtitle="Cadastro, galeria e histórico clínico"
      footer={footer}
      maxWidth={720}
    >
      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        variant="fullWidth"
        sx={{
          mb: 1,
          "& .MuiTab-root": { fontSize: "13.5px", fontWeight: 600, textTransform: "none", color: "text.secondary" },
          "& .Mui-selected": { color: "primary.main" },
          "& .MuiTabs-indicator": { backgroundColor: "primary.main", height: "2px" },
        }}
      >
        <Tab label="Informações" id="tab-0" aria-controls="tabpanel-0" />
        <Tab label="Galeria" id="tab-1" aria-controls="tabpanel-1" />
        <Tab label="Eventos" id="tab-2" aria-controls="tabpanel-2" />
      </Tabs>

      {/* minHeight estável p/ o modal não mudar de tamanho ao alternar as abas */}
      <Box sx={{ minHeight: 440 }}>
        <TabPanel value={tabValue} index={0}>
          <PatientInfoTab formData={formData} allTags={allTags} allPatients={allPatients} onChange={handleChange} onTagsChange={handleTagsChange} onSelectExisting={handleSelectExisting} />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <PatientGalleryTab photos={photos} photoError={photoError} onAddPhotos={handlePhotoChange} onRemovePhoto={handleRemovePhoto} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <PatientEventsTab events={formData.events || []} onAddEvent={handleAddEvent} onDeleteEvent={handleDeleteEvent} />
        </TabPanel>
      </Box>
    </AppModal>
  );
};
